import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock, Handshake, Users, Store, HeartPulse, Plus, X, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Widget de relations bilatérales AITO.PF.
 *
 * Affiche les relations `accepted` autour d'une entité (athlete, coach, club,
 * federation, directory_entry). Si l'utilisateur est owner, il peut :
 *  - proposer une nouvelle relation (partenariat vers un DirectoryEntry)
 *  - accepter/refuser les demandes entrantes en attente
 *  - révoquer une relation acceptée
 *
 * Pour les pros de santé (`directory_entry` de type `sante`) : aucune donnée
 * médicale n'est affichée — uniquement la relation professionnelle.
 */
export function EntityRelationsWidget({
  entityType,
  entityId,
  entityName,
  entityOwnerId,
  title = "Partenaires",
  icon: Icon = Handshake,
}) {
  const { user } = useAuth();
  const [outgoing, setOutgoing] = useState([]); // links avec entityId comme source
  const [incoming, setIncoming] = useState([]); // links avec entityId comme target
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const isOwner = !!user && entityOwnerId && entityOwnerId === user.id;
  const isStaff = !!user && (user.roles || []).some((r) => ["admin", "copf"].includes(r));
  const canManage = isOwner || isStaff;

  const load = () => {
    setLoading(true);
    const status = canManage ? "" : "accepted";
    Promise.all([
      api.get("/entity-links", { params: { source_type: entityType, source_id: entityId, status, limit: 100 } }),
      api.get("/entity-links", { params: { target_type: entityType, target_id: entityId, status, limit: 100 } }),
    ])
      .then(([resOut, resIn]) => {
        setOutgoing(resOut.data?.items || []);
        setIncoming(resIn.data?.items || []);
      })
      .catch(() => { setOutgoing([]); setIncoming([]); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (entityId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const acceptedOutgoing = outgoing.filter((l) => l.status === "accepted");
  const acceptedIncoming = incoming.filter((l) => l.status === "accepted");
  const pendingIncoming = incoming.filter((l) => l.status === "pending");
  const pendingOutgoing = outgoing.filter((l) => l.status === "pending");

  const handleAccept = async (linkId) => {
    try {
      await api.patch(`/entity-links/${linkId}`, { action: "accept" });
      toast.success("Relation acceptée.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const handleReject = async (linkId) => {
    try {
      await api.patch(`/entity-links/${linkId}`, { action: "reject" });
      toast.success("Relation refusée.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const handleRevoke = async (linkId) => {
    if (!window.confirm("Révoquer cette relation ?")) return;
    try {
      await api.delete(`/entity-links/${linkId}`);
      toast.success("Relation révoquée.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const total = acceptedOutgoing.length + acceptedIncoming.length;

  return (
    <Card className="p-4 sm:p-5" data-testid="entity-relations-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
          <h3 className="font-display font-semibold text-sm">{title}</h3>
          {total > 0 && <Badge variant="secondary" className="text-[10px]">{total}</Badge>}
        </div>
        {canManage && entityType !== "directory_entry" && (
          <Button size="sm" variant="outline" onClick={() => setDialogOpen(true)} data-testid="btn-propose-relation">
            <Plus className="h-3.5 w-3.5 mr-1" /> Proposer une relation
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-muted-foreground">Chargement...</p>
      ) : total === 0 && pendingIncoming.length === 0 && pendingOutgoing.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          Aucune relation pour le moment.
          {canManage && entityType !== "directory_entry" && " Cliquez sur « Proposer une relation » pour lier un partenaire (boutique, kiné, coach…)."}
        </p>
      ) : (
        <div className="space-y-3">
          {/* Relations acceptées sortantes */}
          {acceptedOutgoing.length > 0 && (
            <RelationsGroup
              label="Partenaires validés"
              icon={CheckCircle2}
              links={acceptedOutgoing}
              direction="outgoing"
              canManage={canManage}
              onRevoke={handleRevoke}
            />
          )}

          {/* Relations acceptées entrantes */}
          {acceptedIncoming.length > 0 && (
            <RelationsGroup
              label={entityType === "directory_entry" ? "Athlètes / clubs partenaires" : "Sollicité par"}
              icon={CheckCircle2}
              links={acceptedIncoming}
              direction="incoming"
              canManage={canManage}
              onRevoke={handleRevoke}
            />
          )}

          {/* Demandes entrantes en attente */}
          {canManage && pendingIncoming.length > 0 && (
            <PendingIncoming links={pendingIncoming} onAccept={handleAccept} onReject={handleReject} />
          )}

          {/* Demandes sortantes en attente */}
          {canManage && pendingOutgoing.length > 0 && (
            <PendingOutgoing links={pendingOutgoing} onRevoke={handleRevoke} />
          )}
        </div>
      )}

      <ProposeRelationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        sourceType={entityType}
        sourceId={entityId}
        sourceName={entityName}
        onCreated={() => { setDialogOpen(false); load(); }}
      />
    </Card>
  );
}

function targetUrl(link) {
  const kind = link.target_type;
  const slug = link.target_slug || link.target_id;
  if (kind === "athlete") return `/athletes/${slug}`;
  if (kind === "coach") return `/coaches/${slug}`;
  if (kind === "club") return `/clubs/${slug}`;
  if (kind === "federation") return `/federations/${slug}`;
  if (kind === "directory_entry") return `/annuaire/boutiques-services/${slug}`;
  return "#";
}

function sourceUrl(link) {
  const kind = link.source_type;
  const slug = link.source_slug || link.source_id;
  if (kind === "athlete") return `/athletes/${slug}`;
  if (kind === "coach") return `/coaches/${slug}`;
  if (kind === "club") return `/clubs/${slug}`;
  if (kind === "federation") return `/federations/${slug}`;
  if (kind === "directory_entry") return `/annuaire/boutiques-services/${slug}`;
  return "#";
}

function RelationsGroup({ label, icon: Icon, links, direction, canManage, onRevoke }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
        <Icon className="h-3 w-3 text-[hsl(var(--aito-lagoon))]" />
        <span>{label}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {links.map((l) => {
          const name = direction === "outgoing"
            ? (l.target_name_current || l.target_name_snapshot || "Entité")
            : (l.source_name || "Entité");
          const url = direction === "outgoing" ? targetUrl(l) : sourceUrl(l);
          return (
            <div
              key={l.id}
              className="inline-flex items-center gap-1.5 text-xs font-medium py-1 pl-2.5 pr-2 rounded-full border border-[hsl(var(--aito-lagoon)/0.3)] bg-[hsl(var(--aito-lagoon)/0.06)]"
              data-testid={`relation-${l.id}`}
            >
              <Link to={url} className="hover:underline">{name}</Link>
              {canManage && (
                <button
                  onClick={() => onRevoke(l.id)}
                  aria-label="Révoquer"
                  className="ml-1 text-muted-foreground hover:text-destructive"
                  data-testid={`revoke-${l.id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PendingIncoming({ links, onAccept, onReject }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-orange-600 mb-1.5">
        <Clock className="h-3 w-3" />
        <span>Demandes en attente</span>
      </div>
      <div className="space-y-2">
        {links.map((l) => (
          <div
            key={l.id}
            className="flex items-center justify-between gap-2 p-2 rounded-md bg-orange-500/5 border border-orange-500/20"
            data-testid={`pending-${l.id}`}
          >
            <div className="text-xs">
              <span className="font-semibold">{l.source_name || "Une entité"}</span>
              <span className="text-muted-foreground"> souhaite être lié·e</span>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onAccept(l.id)} data-testid={`accept-${l.id}`}>Accepter</Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onReject(l.id)}>Refuser</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PendingOutgoing({ links, onRevoke }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
        <Clock className="h-3 w-3" />
        <span>Vos demandes en attente</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {links.map((l) => (
          <div
            key={l.id}
            className="inline-flex items-center gap-1.5 text-xs py-1 pl-2.5 pr-2 rounded-full border border-dashed border-muted-foreground/30 bg-muted/20"
          >
            <span>{l.target_name_current || l.target_name_snapshot || "Entité"}</span>
            <span className="text-muted-foreground text-[10px]">· en attente</span>
            <button onClick={() => onRevoke(l.id)} className="ml-1 text-muted-foreground hover:text-destructive">
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Dialog de proposition d'une nouvelle relation vers un DirectoryEntry (partenaire).
 * Recherche par nom dans /api/directory.
 */
function ProposeRelationDialog({ open, onOpenChange, sourceType, sourceId, sourceName, onCreated }) {
  const [query, setQuery] = useState("");
  const [type, setType] = useState("all");
  const [results, setResults] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) { setQuery(""); setResults([]); return; }
    const params = { limit: 20 };
    if (query.trim()) params.q = query.trim();
    if (type !== "all") params.type = type;
    api.get("/directory", { params })
      .then((res) => setResults(res.data?.items || []))
      .catch(() => setResults([]));
  }, [open, query, type]);

  const roleForSource = {
    athlete: "athlete_partnered_with",
    coach: "coach_partnered_with",
    club: "club_partnered_with",
    federation: "federation_partnered_with",
  }[sourceType];

  const proposeLink = async (targetId, targetName) => {
    if (!roleForSource) {
      toast.error("Type d'entité source non supporté pour ce type de relation.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/entity-links", {
        source_type: sourceType,
        source_id: sourceId,
        target_type: "directory_entry",
        target_id: targetId,
        role: roleForSource,
        submit: true,
      });
      toast.success(`Demande envoyée à ${targetName}.`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la proposition");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Proposer une relation</DialogTitle>
          <DialogDescription>
            Reliez <strong>{sourceName || "cette entité"}</strong> à un partenaire de l'annuaire AITO.PF (boutique, professionnel de santé, prestataire).
            La relation devient publique après validation par l'autre partie.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un partenaire..."
              data-testid="search-partner"
            />
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                <SelectItem value="sante">Santé & Performance</SelectItem>
                <SelectItem value="boutique">Boutiques & Services</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="max-h-96 overflow-auto space-y-1.5 pr-1">
            {results.length === 0 ? (
              <p className="text-xs text-muted-foreground italic p-4 text-center">
                Aucun partenaire trouvé. Vous pouvez inviter un partenaire à créer sa fiche AITO.PF.
              </p>
            ) : results.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors"
                data-testid={`partner-result-${r.slug}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {r.type === "sante" ? <HeartPulse className="h-4 w-4 text-[hsl(var(--aito-lagoon))] shrink-0" /> : <Store className="h-4 w-4 text-[hsl(var(--aito-gold))] shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{r.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {[r.category?.replace(/_/g, " "), r.commune].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
                <Button size="sm" disabled={creating} onClick={() => proposeLink(r.id, r.name)} data-testid={`propose-${r.slug}`}>
                  Proposer
                </Button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EntityRelationsWidget;
